import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trans } from '@lingui/react/macro';

export const PAGE_SIZES = [10, 25, 50, 100];
const pageSizeItems = PAGE_SIZES.map(String);
const PAGE_SIZE_SELECT_ITEMS = pageSizeItems.map((size) => ({ value: size, label: size }));

export type PaginatedResponse<T> = {
	items: Array<T>;
	totalCount: number;
	totalPages: number;
	currentPage: number;
	pageSize: number;
	nextPage: number | null;
	previousPage: number | null;
};

/**
 * Converts the vadio backend pagination shape into the generic PaginatedResponse.
 */
export function toPaginatedResponse<T>(response: {
	data: T[];
	pagination: {
		totalCount: number;
		totalPages: number;
		currentPage: number;
		limit: number;
		hasNextPage: boolean;
	};
}): PaginatedResponse<T> {
	const { data, pagination: p } = response;
	return {
		items: data,
		totalCount: p.totalCount,
		totalPages: p.totalPages,
		currentPage: p.currentPage,
		pageSize: p.limit,
		nextPage: p.hasNextPage ? p.currentPage + 1 : null,
		previousPage: p.currentPage > 1 ? p.currentPage - 1 : null,
	};
}

export type ItemPage<T> = {
	items: T[];
	total: number;
	page: number;
	pageSize: number;
};

export function itemPageToPaginated<T>(page: ItemPage<T>): PaginatedResponse<T> {
	const totalPages = Math.max(1, Math.ceil(page.total / page.pageSize));
	return {
		items: page.items,
		totalCount: page.total,
		totalPages,
		currentPage: page.page,
		pageSize: page.pageSize,
		nextPage: page.page < totalPages ? page.page + 1 : null,
		previousPage: page.page > 1 ? page.page - 1 : null,
	};
}

interface DataPaginationProps<T> {
	paginatedData: PaginatedResponse<T>;
	onPageChange: (page: number) => void;
	onPageSizeChange: (size: number) => void;
	className?: string;
	disabled?: boolean;
}

export function DataPagination<T>({
	paginatedData,
	onPageChange,
	onPageSizeChange,
	className,
	disabled,
}: DataPaginationProps<T>) {
	const { currentPage, totalPages, totalCount, pageSize, nextPage, previousPage } = paginatedData;

	const startItem = (currentPage - 1) * pageSize + 1;
	const endItem = Math.min(currentPage * pageSize, totalCount);

	const visiblePages = React.useMemo(() => {
		if (totalPages <= 1) return [1];

		const delta = 2;
		const range: number[] = [];
		const rangeWithDots: Array<number | string> = [];

		for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
			range.push(i);
		}

		if (currentPage - delta > 2) {
			rangeWithDots.push(1, '...');
		} else {
			rangeWithDots.push(1);
		}

		rangeWithDots.push(...range);

		if (currentPage + delta < totalPages - 1) {
			rangeWithDots.push('...', totalPages);
		} else {
			rangeWithDots.push(totalPages);
		}

		return rangeWithDots;
	}, [currentPage, totalPages]);

	return (
		<div
			data-testid="data-pagination"
			className={cn('flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:justify-between', className)}
		>
			<div className="hidden items-center gap-1.5 sm:flex">
				<p className="text-xs text-muted-foreground">
					{startItem}-{endItem} <Trans>of</Trans> {totalCount}
				</p>
			</div>

			<div className="flex items-center gap-2">
				<div className="hidden sm:flex items-center gap-1.5">
					<p className="text-xs text-muted-foreground">
						<Trans>Rows</Trans>
					</p>
					<Select
						value={pageSize.toString()}
						onValueChange={(value) => onPageSizeChange(Number(value))}
						disabled={disabled}
						items={PAGE_SIZE_SELECT_ITEMS}
					>
						<SelectTrigger className="w-20 text-xs">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{pageSizeItems.map((size) => (
								<SelectItem key={size} value={size}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="flex items-center gap-0.5">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(1)}
						disabled={previousPage === null || disabled}
						className="h-8 w-8 sm:h-7 sm:w-7 p-0"
					>
						<ChevronsLeft className="h-3.5 w-3.5" />
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							if (previousPage !== null) onPageChange(previousPage);
						}}
						disabled={previousPage === null || disabled}
						className="h-8 w-8 sm:h-7 sm:w-7 p-0"
					>
						<ChevronLeft className="h-3.5 w-3.5" />
					</Button>

					{/* Mobile: simple page indicator */}
					<span className="flex sm:hidden items-center px-2 text-xs text-muted-foreground tabular-nums">
						{currentPage}/{totalPages}
					</span>

					{/* Desktop: full page numbers */}
					{visiblePages.map((page, index) => (
						<React.Fragment key={index}>
							{page === '...' ? (
								<span className="hidden sm:inline px-1 text-xs text-muted-foreground">...</span>
							) : (
								<Button
									variant={currentPage === page ? 'default' : 'outline'}
									size="sm"
									onClick={() => onPageChange(page as number)}
									className="hidden sm:inline-flex h-7 min-w-7 px-1.5 text-xs"
									disabled={disabled}
								>
									{page}
								</Button>
							)}
						</React.Fragment>
					))}

					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							if (nextPage !== null) onPageChange(nextPage);
						}}
						disabled={nextPage === null || disabled}
						className="h-8 w-8 sm:h-7 sm:w-7 p-0"
					>
						<ChevronRight className="h-3.5 w-3.5" />
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(totalPages)}
						disabled={nextPage === null || disabled}
						className="h-8 w-8 sm:h-7 sm:w-7 p-0"
					>
						<ChevronsRight className="h-3.5 w-3.5" />
					</Button>
				</div>
			</div>
		</div>
	);
}
